import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { FaLinkedin, FaFacebookF, FaTwitter, FaChevronDown } from 'react-icons/fa';
import { IoSend } from 'react-icons/io5';

import logoImg from '../../../assets/logo-chinadolphins.png';
import css from './FooterBar.module.css';

const PLATFORM_LINKS = [
  { label: 'About Us', to: '/about-us' },
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'Payment & Transaction Security', to: '/payment-transaction-security' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Become a Sourcing Agent', to: '/become-agent' },
  { label: 'Factory Partnership Program', to: '/factory-partnership' },
];

const LEGAL_LINKS = [
  { label: 'Terms of Service', to: '/terms-of-service' },
  { label: 'Sourcing Agent Terms', to: '/agent-terms' },
  { label: 'Privacy & Data Policy', to: '/privacy-policy' },
  { label: 'Refund & Dispute Resolution', to: '/refund-dispute-policy' },
  { label: 'Global Code of Ethics', to: '/code-of-ethics' },
  { label: 'Global Trade & AML Compliance', to: '/trade-aml-compliance' },
];

const SOCIALS = [
  { Icon: FaLinkedin, label: 'LinkedIn', href: 'https://linkedin.com' },
  { Icon: FaFacebookF, label: 'Facebook', href: 'https://facebook.com' },
  { Icon: FaTwitter, label: 'Twitter', href: 'https://twitter.com' },
];

/**
 * Accordion link column — expands/collapses on mobile, always open on desktop.
 */
const LinkCol = ({ title, links }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={css.col}>
      <button
        type="button"
        className={css.colTitleBtn}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className={css.colTitle}>{title}</span>
        <FaChevronDown
          size={11}
          className={classNames(css.chevron, { [css.chevronOpen]: open })}
        />
      </button>
      <ul className={classNames(css.colList, { [css.colListOpen]: open })}>
        {links.map(({ label, to, href }) => (
          <li key={label}>
            {to ? (
              <Link to={to} className={css.colLink}>
                {label}
              </Link>
            ) : (
              <a href={href || '/'} className={css.colLink} target="_blank" rel="noopener noreferrer">
                {label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

LinkCol.propTypes = {
  title: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.string,
      href: PropTypes.string,
    })
  ).isRequired,
};

/**
 * Footer component for ChinaDolphins.
 */
const Footer = props => {
  const { className, rootClassName } = props;
  const classes = classNames(rootClassName || css.footer, className);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = e => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 4000);
    }
  };

  return (
    <footer className={classes}>
      <div className={css.inner}>
        {/* ── Brand column ── */}
        <div className={css.brand}>
          <div className={css.logoWrap}>
            <Link to="/">
              <img src={logoImg} alt="ChinaDolphins" className={css.logoImg} />
            </Link>
          </div>
          <p className={css.tagline}>
            Connecting the world with China through trust, transparency, and technology.
          </p>
          <div className={css.socials}>
            {SOCIALS.map(({ Icon, label, href }) => (
              <a
                key={label}
                href={href}
                className={css.socialIcon}
                aria-label={label}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon size={14} />
              </a>
            ))}
          </div>
        </div>

        {/* ── Platform Links ── */}
        <LinkCol title="Platform" links={PLATFORM_LINKS} />

        {/* ── Legal & Trust Links ── */}
        <LinkCol title="Legal & Trust" links={LEGAL_LINKS} />

        {/* ── Newsletter column ── */}
        <div className={css.col}>
          <h4 className={css.colTitle}>Newsletter</h4>
          <p className={css.newsletterDesc}>
            Subscribe to get the latest manufacturing updates and sourcing tips.
          </p>
          {subscribed ? (
            <div style={{ color: '#86efac', fontSize: '13px', padding: '8px 0' }}>
              ✓ Thank you for subscribing!
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className={css.emailRow}>
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={css.emailInput}
                autoComplete="email"
                required
              />
              <button type="submit" className={css.emailBtn} aria-label="Subscribe">
                <IoSend size={16} />
              </button>
            </form>
          )}
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className={css.bottomBar}>
        <p className={css.copyright}>
          © {new Date().getFullYear()} ChinaDolphins (MTN GLOBAL LIMITED). All rights reserved.
        </p>
      </div>
    </footer>
  );
};

Footer.defaultProps = { className: null, rootClassName: null };
Footer.propTypes = { className: PropTypes.string, rootClassName: PropTypes.string };

export default Footer;
